import { DialogProgrammatic as Dialog } from "buefy";
import { ToastProgrammatic as Toast } from "buefy";

/**
 * Extracts and decodes filename from Content-Disposition header
 * Supports both RFC 5987 UTF-8 encoding and standard filename parameter
 * @param contentDisposition - The Content-Disposition header value
 * @param fallbackFileName - Fallback filename if extraction fails
 * @returns Decoded filename
 */
export function extractFilenameFromHeader(
  contentDisposition: string | undefined,
  fallbackFileName: string
): string {
  let finalFileName = fallbackFileName;

  if (contentDisposition) {
    // Try RFC 5987 UTF-8 encoding first (filename*=UTF-8''...)
    const utf8Match = contentDisposition.match(/filename\*=UTF-8''([^;\n]*)/i);
    if (utf8Match && utf8Match[1]) {
      try {
        finalFileName = decodeURIComponent(utf8Match[1]);
        return finalFileName;
      } catch (e) {
        console.warn("Failed to decode UTF-8 filename:", utf8Match[1]);
      }
    }

    // Try standard filename parameter
    const fileNameMatch = contentDisposition.match(
      /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/
    );
    if (fileNameMatch && fileNameMatch[1]) {
      const extractedName = fileNameMatch[1].replace(/['"]/g, "").trim();

      // Check if it's already properly encoded (not quoted-printable)
      // If it contains =XX patterns (quoted-printable), use fallback
      if (/=[0-9A-F]{2}/.test(extractedName)) {
        console.warn(
          "Detected quoted-printable encoding, using fallback filename"
        );
        return fallbackFileName;
      }

      try {
        // Try to decode if it's URL encoded
        const decoded = decodeURIComponent(extractedName);
        // Use decoded version if it's different from the original
        if (decoded !== extractedName) {
          return decoded;
        }
        return extractedName;
      } catch (e) {
        console.warn("Failed to decode filename, using as-is:", extractedName);
        return extractedName;
      }
    }
  }

  return finalFileName;
}

/**
 * Cleans up filename by removing unwanted parts
 * @param fileName - The filename to clean
 * @returns Cleaned filename
 */
export function cleanupFileName(fileName: string): string {
  return fileName.replaceAll("/", "").replaceAll("media", "");
}

/**
 * Downloads a blob as a file with proper filename handling
 * @param blob - The blob data to download
 * @param contentDisposition - Content-Disposition header value
 * @param fallbackFileName - Fallback filename
 */
export function downloadBlobAsFile(
  blob: Blob,
  contentDisposition: string | undefined,
  fallbackFileName: string
): void {
  const downloadUrl = window.URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = downloadUrl;

  const extractedFileName = extractFilenameFromHeader(
    contentDisposition,
    fallbackFileName
  );
  const cleanedFileName = cleanupFileName(extractedFileName);

  link.setAttribute("download", cleanedFileName);
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(downloadUrl);
}

/**
 * Handles file download with toast notification and error handling
 * @param httpClient - Axios HTTP client instance
 * @param url - Download URL
 * @param fallbackFileName - Fallback filename
 * @param errorTitle - Error dialog title
 * @param errorMessage - Error dialog message
 */
export async function handleFileDownload(
  httpClient: any,
  url: string,
  fallbackFileName: string,
  errorTitle = "Export not possible",
  errorMessage = "There was an error downloading the file. Please contact your Customer Account Manager."
): Promise<void> {
  const successToast = Toast.open({
    indefinite: true,
    message: "Download in Progress!",
    type: "is-success"
  });

  try {
    const response = await httpClient.get(url, { responseType: "blob" });
    downloadBlobAsFile(
      new Blob([response.data]),
      response.headers["content-disposition"],
      fallbackFileName
    );
    successToast.close();
  } catch (err) {
    successToast.close();
    Dialog.alert({
      title: errorTitle,
      message: errorMessage,
      type: "is-danger",
      ariaRole: "alertdialog",
      ariaModal: true
    });
    throw err;
  }
}
